import { useNavigate, useSearch } from '@tanstack/react-router'
import {
    ChartBarIcon,
    AcademicCapIcon,
    TrophyIcon,
    PlayIcon,
    CommandLineIcon,
} from '@heroicons/react/24/outline'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button } from '@duck/ui'
import { useAuth } from '@/hooks/useAuth'
import { useEffect, useRef } from 'react'
import { Trans } from 'react-i18next'
import { useTar } from '@/modules/i18n/useTar'
import { WaitingList } from '../WaitingList/WaitingList'
import { Logo } from '../Misc/Logo'

const FEATURE_KEYS = ['realSituations', 'learnByDoing', 'personalizedFeedback'] as const

export const HomePage = () => {
    const { t } = useTar()
    const { openSignupDialog, isAuthenticated, isInitializing } = useAuth()
    const navigate = useNavigate()
    const search = useSearch({ from: '/' })
    const waitingListRef = useRef<HTMLDivElement>(null)

    // Check for token parameter and open signup dialog (only after auth is ready so we know if user is logged in)
    useEffect(() => {
        if (isInitializing || !(search.token || search.atoken)) {
            return
        }
        const timeout = setTimeout(() => {
            openSignupDialog(search.token || search.atoken)
        }, 256)
        return () => clearTimeout(timeout)
    }, [search.token, search.atoken, openSignupDialog, isInitializing])

    const features = [
        { icon: ChartBarIcon, key: FEATURE_KEYS[0] },
        { icon: AcademicCapIcon, key: FEATURE_KEYS[1] },
        { icon: TrophyIcon, key: FEATURE_KEYS[2] },
    ]

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-6xl space-y-8 mt-8">
                {/* Hero Section */}
                <div className="text-center space-y-6">
                    <div className="space-y-4">
                        <h1 className="text-4xl md:text-6xl font-bold text-foreground">
                            {t('home.heroTitle')}
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            {t('home.heroSubtitle')}
                        </p>
                    </div>
                    <div className="h-1 w-full border-t border-border" />
                    {!isAuthenticated ? (
                        <div ref={waitingListRef}>
                            <WaitingList />
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
                            <Button
                                onClick={() => {
                                    navigate({ to: '/challenges/available' })
                                }}
                                size="lg"
                                className="flex items-center gap-2 px-6 py-6 bg-accent hover:bg-accent/80 text-foreground text-lg font-medium rounded-lg transition-colors duration-200"
                            >
                                <PlayIcon className="w-6 h-6" />
                                <span>{t('home.startAnalyzing')}</span>
                            </Button>

                        </div>
                    )}
                </div>
                {/* Features Section */}
                <div className="space-y-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-foreground mb-4">
                            <Trans i18nKey="home.whyTitle" components={{ logo: <Logo /> }} />
                        </h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            {t('home.whySubtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, index) => {
                            const IconComponent = feature.icon
                            return (
                                <div
                                    key={index}
                                    className="bg-card rounded-lg shadow-sm border border-border p-6 text-center hover:shadow-md transition-shadow duration-200"
                                >
                                    <div className="flex justify-center mb-4">
                                        <div className="p-3 bg-accent/10 rounded-full">
                                            <IconComponent className="w-8 h-8 text-accent" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-semibold text-foreground mb-3">
                                        {t(`home.features.${feature.key}.title`)}
                                    </h3>
                                    <p className="text-muted-foreground leading-relaxed">
                                        {t(`home.features.${feature.key}.description`)}
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Call to Action Section */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            <div className="p-4 bg-accent/10 rounded-full">
                                <CommandLineIcon className="w-12 h-12 text-accent" />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">{t('home.manifesto.title')}</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            {t('home.manifesto.subtitle')}
                        </p>
                        <Accordion type="single" collapsible className="w-full max-w-xl mx-auto text-start">
                            <AccordionItem value="manifesto" className="border-none">
                                <AccordionTrigger className="text-lg font-semibold text-foreground border-0 justify-center text-center">
                                    {t('home.manifesto.readManifesto')}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="space-y-4 text-muted-foreground leading-relaxed text-start">
                                        <p>{t('home.manifesto.p1')}</p>
                                        <p>{t('home.manifesto.p2')}</p>
                                        <p>{t('home.manifesto.p3')}</p>
                                        <p><Trans i18nKey="home.manifesto.p4" components={{ 1: <em /> }} /></p>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading1')}</h3>
                                            <p>{t('home.manifesto.p5')}</p>
                                            <blockquote className="border-s-2 border-border ps-3 italic text-foreground">{t('home.manifesto.quote1')}</blockquote>
                                            <p>{t('home.manifesto.p6')}</p>
                                            <p>{t('home.manifesto.p7')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading2')}</h3>
                                            <p>{t('home.manifesto.p8')}</p>
                                            <p>{t('home.manifesto.p9')}</p>
                                            <p>{t('home.manifesto.p10')}</p>
                                            <p>{t('home.manifesto.p11')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading3')}</h3>
                                            <p>{t('home.manifesto.p12')}</p>
                                            <p>{t('home.manifesto.p13')}</p>
                                            <p>{t('home.manifesto.p14')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading4')}</h3>
                                            <p>{t('home.manifesto.p15')}</p>
                                            <p>{t('home.manifesto.p16')}</p>
                                            <p>{t('home.manifesto.p17')}</p>
                                            <p>{t('home.manifesto.p18')}</p>
                                            <p>{t('home.manifesto.p19')}</p>
                                            <p>{t('home.manifesto.p20')}</p>
                                            <p>{t('home.manifesto.p21')}</p>
                                            <p>{t('home.manifesto.p22')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading5')}</h3>
                                            <p>{t('home.manifesto.p23')}</p>
                                            <p>{t('home.manifesto.p24')}</p>
                                            <p>{t('home.manifesto.p25')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading6')}</h3>
                                            <p>{t('home.manifesto.p26')}</p>
                                            <p>{t('home.manifesto.p27')}</p>
                                            <p>{t('home.manifesto.p28')}</p>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold text-foreground">{t('home.manifesto.heading7')}</h3>
                                            <p>{t('home.manifesto.p29')}</p>
                                            <p>{t('home.manifesto.p30')}</p>
                                            <p>{t('home.manifesto.p31')}</p>
                                            <p>{t('home.manifesto.p32')}</p>
                                            <p>{t('home.manifesto.p33')}</p>
                                            <p>{t('home.manifesto.p34')}</p>
                                        </div>

                                        <div className="flex justify-center pt-4">
                                            <Button
                                                onClick={() => {
                                                    if (isAuthenticated) {
                                                        navigate({ to: '/challenges/available' })
                                                    } else {
                                                        waitingListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                                    }
                                                }}
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/80 text-foreground text-lg font-medium rounded-lg transition-colors duration-200"
                                            >
                                                <PlayIcon className="w-6 h-6" />
                                                <span>{t('home.getStartedNow')}</span>
                                            </Button>
                                        </div>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>
                </div>

                {/* Footer Note */}
                <div className="text-center pb-8">
                    <p className="text-muted text-sm">
                        {t('home.footerNote')}
                    </p>
                </div>
            </div>
        </div>
    )
}
